import type { filtersSchema } from '@/shared/components/filter/validation'
import type { BadgeProps } from '@/shared/components/ui/badge'
import type {
	AnchorStrategyCreateModel,
	AnchorStrategyModel,
	AnchorStrategyUpdateModel,
} from '@/shared/generated'
import type { z } from 'zod'
import type { StrategyModalFormData } from '../model/types'

import { useMemo, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { formatDistanceToNow } from 'date-fns'
import { find } from 'lodash/fp'
import { UndoIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { UsernameById } from '@/entities/user'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { FormProvider } from '@/shared/components/HookForm'
import { Badge } from '@/shared/components/ui/badge'
import { Blanket } from '@/shared/components/ui/blanket'
import { Button } from '@/shared/components/ui/button'
import {
	DescriptionDetail,
	DescriptionGroup,
	DescriptionList,
	DescriptionTerm,
} from '@/shared/components/ui/description-list'
import { Loader } from '@/shared/components/ui/loader'
import {
	Modal,
	ModalBody,
	ModalClose,
	ModalContent,
	ModalFooter,
	ModalHeader,
} from '@/shared/components/ui/modal'
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import { cn } from '@/shared/lib/utils'
import { formatNumber } from '@/shared/tools/locale'

import { getStrategyModalTabs } from '../lib/getStrategyModalTabs'
import { getAnchorTypeValues } from '../lib/helpers'
import { useCreateAnchorStrategy } from '../lib/useCreateAnchorStrategy'
import { useUpdateAnchorStrategy } from '../lib/useUpdateAnchorStrategy'
import { getFormSchema } from '../model/contract'
import { StrategyHeader } from './StrategyModalHeader'

type BadgeVariant = BadgeProps['variant']

export function StrategyModal({
	id,
	name,
	products_count = 0,
	anchor_type,
	optimization_group_ids,
	settings,
	updated_at,
	isOpen,
	close,
	updated_by,
}: Partial<AnchorStrategyModel> & { close: () => void; isOpen: boolean }) {
	const { t } = useTranslation()

	const [currentTab, setCurrentTab] = useState('general')
	const tabs = useMemo(() => getStrategyModalTabs(t), [t])
	const { handleCreateStrategy, isPending: isCreatingPending } =
		useCreateAnchorStrategy(close)
	const { handleUpdateStrategy, isPending: isUpdatingPending } =
		useUpdateAnchorStrategy(close)
	const TabContent = useMemo(
		() => find({ key: currentTab }, tabs)?.TabContent ?? 'div',
		[currentTab, tabs]
	)
	const isNew = !id
	const methods = useForm<StrategyModalFormData>({
		defaultValues: {
			name: name || '',
			optimization_group_ids: optimization_group_ids || [],
			settings: {
				groups:
					settings?.groups.map(
						({ filters, aggregation, clusterization, ...rest }) => ({
							filters: (filters || []) as z.infer<typeof filtersSchema>,
							aggregation: aggregation || [],
							clusterization: clusterization ?? [],
							...rest,
						})
					) || [],

				indexes: {
					index_type: settings?.indexes?.index_type || 'mul',
					columns: settings?.indexes?.columns || [],
				},
				anchor_selection: {
					filters: settings?.anchor_selection?.filters ?? [],
					sorting: settings?.anchor_selection?.sorting?.length
						? settings.anchor_selection.sorting
						: [{ name: 'sku', direction: 'asc' as const }],
				},
				general: settings?.general ?? {
					propagate_generated_anchors: true,
					cleanup_generated_anchors: false,
				},
			},
		},
		resolver: zodResolver(getFormSchema(t)),
	})
	const _handleSubmit = (data: z.infer<ReturnType<typeof getFormSchema>>) => {
		if (isNew) {
			return handleCreateStrategy({
				...data,
				anchor_type,
			} as AnchorStrategyCreateModel)
		}
		handleUpdateStrategy({
			...data,
			id,
		} as AnchorStrategyUpdateModel & { id: string })
	}
	const handleSubmit = methods.handleSubmit(_handleSubmit)
	const anchorTypeValues = useMemo(
		() => getAnchorTypeValues(anchor_type!, t),
		[anchor_type, t]
	)

	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<ModalContent
				className='h-[calc(100vh-2rem)] max-w-4xl'
				data-testid='anchor-strategy-modal'
			>
				<ErrorBoundary>
					<FormProvider
						className='flex flex-1 flex-col gap-2 overflow-hidden'
						methods={methods}
						onSubmit={handleSubmit}
					>
						<ModalHeader className='space-y-2'>
							<StrategyHeader />
							<DescriptionList className='flex gap-16'>
								<DescriptionGroup>
									<DescriptionTerm className='text-xs'>
										{t('anchoring_type')}
									</DescriptionTerm>
									<DescriptionDetail>
										<Badge variant={anchorTypeValues!.variant as BadgeVariant}>
											<span>{anchorTypeValues?.label}</span>
										</Badge>
									</DescriptionDetail>
								</DescriptionGroup>
								{!isNew && <ProductCount items={products_count} />}
								<OptimizationGroupCount
									items={optimization_group_ids?.length || 0}
								/>
								{!isNew && (
									<LastModified
										updated_at={updated_at!}
										updated_by={updated_by!}
									/>
								)}
							</DescriptionList>
						</ModalHeader>
						<Blanket
							className='absolute flex items-center justify-center rounded-lg bg-white/50 backdrop-blur-lg dark:bg-black/50'
							isTinted={isUpdatingPending || isCreatingPending}
						>
							<Loader />
						</Blanket>
						<Tabs
							data-testid='anchor-strategy-modal-tab'
							value={currentTab}
							onValueChange={(value) => setCurrentTab(value)}
						>
							<TabsList className='cursor-auto gap-2'>
								{tabs.map((tab) => (
									<TabsTrigger
										key={tab.key}
										value={tab.key}
										className={cn({
											'text-destructive!': tab.hasError(
												methods.formState.errors
											),
										})}
									>
										{tab.label}
									</TabsTrigger>
								))}
							</TabsList>
						</Tabs>
						<ModalBody className='overflow-y-auto'>
							<TabContent />
						</ModalBody>
						<ModalFooter className='items-center justify-between!'>
							<ModalClose asChild>
								<Button>{t('general_cancel')}</Button>
							</ModalClose>
							<div className='flex items-center space-x-2'>
								{methods.formState.isDirty && !isNew && (
									<Button
										data-testid='pc-form-revert-changes'
										onClick={() => methods.reset()}
										variant='ghost'
									>
										<UndoIcon />
										{t('app.revert_changes')}
									</Button>
								)}
								<Button
									variant='warning'
									data-testid='pc-form-save'
									disabled={!isNew && !methods.formState.isDirty}
									onClick={handleSubmit}
								>
									{t('general_save')}
								</Button>
							</div>
						</ModalFooter>
					</FormProvider>
				</ErrorBoundary>
			</ModalContent>
		</Modal>
	)
}
function LastModified({
	updated_at,
	updated_by,
}: {
	updated_at: string
	updated_by: string
}) {
	const { t } = useTranslation()

	return (
		<DescriptionGroup>
			<DescriptionTerm className='text-xs'>
				{t('last_modified_at')}
			</DescriptionTerm>
			<DescriptionDetail className='flex space-x-2 text-xs'>
				{updated_at ? (
					<span className='font-medium'>
						{formatDistanceToNow(new Date(updated_at + 'Z'), {
							addSuffix: true,
						})}
						{updated_at && updated_by && ` ${t('by', { defaultValue: 'by' })} `}
						{updated_by && <UsernameById userId={updated_by} />}
					</span>
				) : (
					'-'
				)}
			</DescriptionDetail>
		</DescriptionGroup>
	)
}
function OptimizationGroupCount({ items }: { items: number }) {
	const { t } = useTranslation()

	return (
		<DescriptionGroup>
			<DescriptionTerm className='text-xs'>
				{t('anchoring_management.strategy.used_in_ogs.title')}
			</DescriptionTerm>
			<DescriptionDetail className='text-xs'>{items}</DescriptionDetail>
		</DescriptionGroup>
	)
}
function ProductCount({ items }: { items: number }) {
	const { t } = useTranslation()

	return (
		<DescriptionGroup>
			<DescriptionTerm className='text-xs'>{t('items')}</DescriptionTerm>
			<DescriptionDetail className='text-xs'>
				{formatNumber(items)}
			</DescriptionDetail>
		</DescriptionGroup>
	)
}
